import { useState } from 'react';
import dayjs from 'dayjs';
import { message } from 'antd';
import { AccountUserListOptionType, SortOptionType, SortType, TableSortOptionType } from './types/options';
import { AccountUser } from './types/accountUser';
import {
  ACCOUNT_USER_LIST_FILTER_OPTION_MODEL,
  ACCOUNT_USER_LIST_SORT_OPTION_MODEL,
  LIST_PAGINATION_MODEL,
} from './models/options';
import { useAccountUserListQuery } from './queries/useAccountUserListQuery';
import toastMsg from '../../../../../lib/toastMsg';
import accountUserToastMsg from '../../../../../constants/service/toastMsg/accountUserToastMsg';
import { NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT } from '../../../../../constants/service/common/utilConstants';
import { UserGradeSelectType } from './types/column';
import { useUserGradesQuery } from '../../detail/AccountUserDetailGrade/queries/useUserGradesQuery';
import { validateError } from '../../../../../lib/utils/validateError';
import { useRequestDownloadExcelUserListQuery } from './queries/useRequestDownloadExcelUserListQuery';

export interface IAccountUserListViewModel {
  list: AccountUser[];
  totalCount: number;
  options: AccountUserListOptionType;
  gradeList: UserGradeSelectType[];
  getList: (options: AccountUserListOptionType) => void;
  requestDownloadExcel: () => void;
}

export const useAccountUserListViewModel = (): IAccountUserListViewModel => {
  const [filterOption, setFilterOption] = useState(ACCOUNT_USER_LIST_FILTER_OPTION_MODEL);
  const [sortOption, setSortOption] = useState<TableSortOptionType[]>(ACCOUNT_USER_LIST_SORT_OPTION_MODEL);
  const [pagination, setPagination] = useState(LIST_PAGINATION_MODEL);

  const currentSortType: SortType = sortOption[0].desc ? `-${sortOption[0].id}` : sortOption[0].id;
  const tableSortOption: SortOptionType = { sort: currentSortType };

  const { data: accountUserList } = useAccountUserListQuery(
    { ...filterOption, ...tableSortOption, pageSize: pagination.pageSize, page: pagination.pageNum + 1 },
    {
      onSuccess: () => toastMsg.success(accountUserToastMsg.GET_USER_LIST_REQUEST_SUCCESS),
      onError: (err) => {
        toastMsg.error(accountUserToastMsg.GET_USER_LIST_REQUEST_ERROR, err);
      },
    },
  );

  const { data: gradeList } = useUserGradesQuery<UserGradeSelectType[]>({
    options: {
      select: (result) => {
        const gradeList = result.userGrades.map((item) => ({ id: item.grade, name: item.name }));
        return [{ id: null, name: '전체' }, ...gradeList];
      },
    },
  });

  const { refetch: requestDownloadExcelUserList } = useRequestDownloadExcelUserListQuery(filterOption);

  const getList = ({ filterOption, sortOption, pagination }: AccountUserListOptionType) => {
    if (filterOption)
      setFilterOption({
        ...filterOption,
        fromCreateDate: filterOption.fromCreateDate
          ? dayjs(filterOption.fromCreateDate).startOf('day').toISOString()
          : null,
        toCreateDate: filterOption.toCreateDate ? dayjs(filterOption.toCreateDate).endOf('day').toISOString() : null,
      });
    if (sortOption) setSortOption(sortOption);
    if (pagination) setPagination({ pageNum: pagination.pageNum, pageSize: pagination.pageSize });
  };

  const requestDownloadExcel = () => {
    validateError(
      [
        {
          if: !accountUserList || !accountUserList.users.length,
          onError: () => {
            message.error(NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT);
          },
        },
      ],
      async () => {
        await requestDownloadExcelUserList();
      },
    );
  };

  return {
    list: accountUserList?.users || [],
    totalCount: accountUserList?.totalCount || 0,
    options: {
      pagination: {
        pages: Math.ceil((accountUserList?.totalCount || 0) / pagination.pageSize),
        pageNum: pagination.pageNum,
        pageSize: pagination.pageSize,
      },
      sortOption,
      filterOption,
    },
    gradeList: gradeList || [],
    getList,
    requestDownloadExcel,
  };
};
